import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getDramasByIds, getNewsById } from "@/utils/media";
import { news } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams();
  const item = getNewsById(Number(id));
  const [shared, setShared] = useState(false);

  usePageMeta({
    title: item ? `${item.title} - 韩剧TV` : "资讯未找到 - 韩剧TV",
    description: item?.summary ?? "韩剧TV 资讯详情页",
    keywords: item ? `${item.tag},韩剧资讯,${item.title}` : "韩剧资讯",
  });

  if (!item) {
    return (
      <div className="flex w-full flex-col items-center justify-center px-4 py-24 text-center md:py-32">
        <i className="ri-file-unknow-line text-6xl text-foreground-300"></i>
        <h1 className="mt-6 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">内容不存在</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          抱歉，你访问的资讯可能已被移除或链接有误。不妨去看看其他精彩内容。
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/category/news" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 hover:bg-primary-600 cursor-pointer">
            返回资讯列表
          </Link>
          <Link to="/" className="whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer">
            回到首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedNews = news.filter((n) => n.id !== item.id && n.tag === item.tag).slice(0, 3);
  const relatedNewsFallback = news.filter((n) => n.id !== item.id).slice(0, 3);
  const related = relatedNews.length > 0 ? relatedNews : relatedNewsFallback;
  const relatedDramas = getDramasByIds(item.relatedDramaIds);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore, still show feedback
    }
    setShared(true);
    window.setTimeout(() => setShared(false), 2000);
  };

  return (
    <article className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "韩剧资讯", to: "/category/news" },
          { label: item.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/category/news"
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 hover:bg-secondary-200 cursor-pointer"
            >
              {item.tag}
            </Link>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-calendar-line"></i> {item.date}
            </span>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-edit-line"></i> {item.author}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-800 leading-snug text-foreground-950 md:text-4xl">
            {item.title}
          </h1>

          <p className="mt-5 rounded-lg border-l-4 border-primary-400 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {item.summary}
          </p>

          <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-xl bg-background-200">
            <img src={item.cover} alt={item.title} title={`${item.title} - 韩剧资讯`} className="h-full w-full object-cover object-top" />
          </div>

          <div className="mt-8 space-y-5">
            {item.content.map((para, i) => (
              <p key={i} className="text-base leading-8 text-foreground-800">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-y border-background-200/70 py-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">相关剧集：</span>
              {relatedDramas.map((d) => (
                <Link
                  key={d.id}
                  to={`/detail/drama/${d.id}`}
                  className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700 hover:bg-primary-200 cursor-pointer"
                >
                  {d.title}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-share-forward-line"></i> 分享
              </button>
              <button type="button" className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 cursor-pointer" aria-label="点赞">
                <i className="ri-thumb-up-line"></i>
              </button>
            </div>
          </div>

          {shared && (
            <div className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-700">
              <i className="ri-checkbox-circle-line"></i> 链接已复制，快分享给同样爱追剧的朋友吧。
            </div>
          )}

          <section className="mt-12">
            <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">相关推荐</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((n) => (
                <NewsCard key={n.id} item={n} />
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-700 text-foreground-950">
              <i className="ri-play-list-2-line text-primary-500"></i> 热门剧集
            </h3>
            <div className="mt-4 space-y-4">
              {relatedDramas.concat(getDramasByIds([1, 2, 4])).slice(0, 4).map((d) => (
                <DramaCard key={d.id} drama={d} variant="list" />
              ))}
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}